import { useState, useEffect } from 'react';
import {
  Card,
  TextField,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Spinner,
  DataTable,
  Modal,
  EmptyState,
  Checkbox,
} from '@shopify/polaris';
import { fetchJsonWithAuth } from '../utils/fetchWithAuth';

interface FAQManagementProps {
  shop: string;
}

interface FAQ {
  id: number;
  question: string;
  answer: string;
  display_order: number;
}

interface FAQSettings {
  faq_enabled: boolean;
  faq_section_title: string;
  faq_section_subtitle: string;
  faq_bg_color: string;
  faq_text_color: string;
  faq_border_color: string;
}

export default function FAQManagement({ shop }: FAQManagementProps) {
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [faqSettings, setFaqSettings] = useState<FAQSettings>({
    faq_enabled: true,
    faq_section_title: 'Frequently Asked Questions',
    faq_section_subtitle: 'Find answers to common questions',
    faq_bg_color: '#fafbfb',
    faq_text_color: '#202223',
    faq_border_color: '#e1e3e5',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [formData, setFormData] = useState({ question: '', answer: '', display_order: 0 });

  useEffect(() => {
    loadFAQs();
    loadSettings();
  }, [shop]);

  const loadSettings = async () => {
    try {
      const data = await fetchJsonWithAuth(`/api/admin/settings?shop=${encodeURIComponent(shop)}`);
      setFaqSettings({
        faq_enabled: data.faq_enabled === 1 || data.faq_enabled === true,
        faq_section_title: data.faq_section_title || 'Frequently Asked Questions',
        faq_section_subtitle: data.faq_section_subtitle || 'Find answers to common questions',
        faq_bg_color: data.faq_bg_color || '#fafbfb',
        faq_text_color: data.faq_text_color || '#202223',
        faq_border_color: data.faq_border_color || '#e1e3e5',
      });
    } catch (err) {
      console.error('Failed to load FAQ settings:', err);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await fetchJsonWithAuth(`/api/admin/settings?shop=${encodeURIComponent(shop)}`, {
        method: 'PUT',
        body: JSON.stringify({
          faq_enabled: faqSettings.faq_enabled ? 1 : 0,
          faq_section_title: faqSettings.faq_section_title,
          faq_section_subtitle: faqSettings.faq_section_subtitle,
          faq_bg_color: faqSettings.faq_bg_color,
          faq_text_color: faqSettings.faq_text_color,
          faq_border_color: faqSettings.faq_border_color,
        }),
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save settings. Session may have expired - redirecting...');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertToDefault = async () => {
    if (!confirm('Are you sure you want to revert all FAQ section settings and FAQ items to their default values? This will delete all current FAQs and restore the default ones. This cannot be undone.')) {
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Delete all existing FAQs
      await fetchJsonWithAuth(`/api/faq/all?shop=${encodeURIComponent(shop)}`, {
        method: 'DELETE',
      });

      // Create default FAQs
      const defaultFAQs = [
        {
          question: 'How long does shipping take?',
          answer: 'Standard shipping typically takes 7-14 business days. Express shipping options are available at checkout for faster delivery.',
          display_order: 0,
        },
        {
          question: 'How can I track my order?',
          answer: 'Once your order ships, you\'ll receive a tracking number via email. You can use this number to track your package on our shipping carrier\'s website.',
          display_order: 1,
        },
        {
          question: 'What is your return policy?',
          answer: 'We offer a 30-day return policy for all unworn, unused items in their original packaging. Items must be in their original condition with tags attached.',
          display_order: 2,
        },
        {
          question: 'Do you ship internationally?',
          answer: 'Yes, we ship to most countries worldwide. International shipping times and costs vary by destination. Please check your cart for shipping options to your location.',
          display_order: 3,
        },
      ];

      // Create each default FAQ
      for (const faq of defaultFAQs) {
        await fetchJsonWithAuth(`/api/faq/create?shop=${encodeURIComponent(shop)}`, {
          method: 'POST',
          body: JSON.stringify(faq),
        });
      }

      // Reload FAQs list (should now have default FAQs)
      await loadFAQs();

      // Reset settings to defaults
      setFaqSettings({
        faq_enabled: true,
        faq_section_title: 'Frequently Asked Questions',
        faq_section_subtitle: 'Find answers to common questions',
        faq_bg_color: '#fafbfb',
        faq_text_color: '#202223',
        faq_border_color: '#e1e3e5',
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to revert FAQs. Session may have expired - redirecting...');
    } finally {
      setSaving(false);
    }
  };

  const loadFAQs = async () => {
    try {
      const data = await fetchJsonWithAuth(`/api/faq/list?shop=${encodeURIComponent(shop)}`);
      setFaqs(data);
    } catch (err) {
      setError('Failed to load FAQs. Session may have expired - redirecting...');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingFaq(null);
    setFormData({ question: '', answer: '', display_order: faqs.length });
    setModalOpen(true);
  };

  const handleEdit = (faq: FAQ) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      display_order: faq.display_order,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.question.trim() || !formData.answer.trim()) {
      setError('Question and answer are required');
      return;
    }

    try {
      if (editingFaq) {
        await fetchJsonWithAuth(
          `/api/faq/${editingFaq.id}?shop=${encodeURIComponent(shop)}`,
          {
            method: 'PUT',
            body: JSON.stringify(formData),
          }
        );
      } else {
        await fetchJsonWithAuth(`/api/faq/create?shop=${encodeURIComponent(shop)}`, {
          method: 'POST',
          body: JSON.stringify(formData),
        });
      }

      await loadFAQs();
      setModalOpen(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save FAQ. Session may have expired - redirecting...');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      await fetchJsonWithAuth(`/api/faq/${id}?shop=${encodeURIComponent(shop)}`, {
        method: 'DELETE',
      });

      await loadFAQs();
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to delete FAQ. Session may have expired - redirecting...');
    }
  };

  if (loading) {
    return (
      <Card>
        <BlockStack align="center">
          <Spinner size="small" />
        </BlockStack>
      </Card>
    );
  }

  const tableRows = faqs.map((faq) => [
    faq.display_order.toString(),
    faq.question,
    faq.answer.substring(0, 50) + (faq.answer.length > 50 ? '...' : ''),
    <InlineStack key={faq.id} gap="2">
      <Button size="micro" onClick={() => handleEdit(faq)}>
        Edit
      </Button>
      <Button size="micro" destructive onClick={() => handleDelete(faq.id)}>
        Delete
      </Button>
    </InlineStack>,
  ]);

  return (
    <BlockStack gap="500">
      {success && (
        <Banner status="success" onDismiss={() => setSuccess(false)}>
          Settings saved successfully
        </Banner>
      )}

      {error && (
        <Banner status="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {/* Save Settings and Revert Buttons at the top */}
      <div style={{
        marginBottom: '32px',
        padding: '28px',
        backgroundColor: '#f6f6f7',
        borderRadius: '10px',
        border: '3px solid #008060',
        boxShadow: '0 4px 16px rgba(0, 128, 96, 0.2)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px'
      }}>
        <Button
          primary
          onClick={handleSaveSettings}
          loading={saving}
          size="large"
          style={{
            fontSize: '22px',
            fontWeight: '900',
            padding: '22px 48px',
            minWidth: '280px',
            backgroundColor: '#008060',
            borderColor: '#008060',
            boxShadow: '0 6px 16px rgba(0, 128, 96, 0.4)',
            transform: 'scale(1.03)',
            transition: 'all 0.2s ease',
            letterSpacing: '0.5px'
          }}
        >
          💾 Save Settings
        </Button>
        <Button
          onClick={handleRevertToDefault}
          loading={saving}
          size="large"
          tone="critical"
          style={{
            fontSize: '22px',
            fontWeight: '900',
            padding: '22px 48px',
            minWidth: '280px',
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            letterSpacing: '0.5px'
          }}
        >
          🔄 Revert to Default
        </Button>
      </div>

      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h2">
            FAQ Section Settings
          </Text>

          <Checkbox
            label="Enable FAQ Section"
            checked={faqSettings.faq_enabled}
            onChange={(checked) => setFaqSettings({ ...faqSettings, faq_enabled: checked })}
            helpText="Show or hide the FAQ section on the customer page"
          />

          <TextField
            label="Section Title"
            value={faqSettings.faq_section_title}
            onChange={(value) => setFaqSettings({ ...faqSettings, faq_section_title: value })}
            helpText="Main title displayed at the top"
          />

          <TextField
            label="Section Subtitle"
            value={faqSettings.faq_section_subtitle}
            onChange={(value) => setFaqSettings({ ...faqSettings, faq_section_subtitle: value })}
            helpText="Optional subtitle below the title"
          />

          <InlineStack gap="400">
            <div style={{ flex: 1 }}>
              <TextField
                label="Background Color"
                value={faqSettings.faq_bg_color}
                onChange={(value) => setFaqSettings({ ...faqSettings, faq_bg_color: value })}
                type="color"
                helpText="Section background"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label="Text Color"
                value={faqSettings.faq_text_color}
                onChange={(value) => setFaqSettings({ ...faqSettings, faq_text_color: value })}
                type="color"
                helpText="Text color"
              />
            </div>
            <div style={{ flex: 1 }}>
              <TextField
                label="Border Color"
                value={faqSettings.faq_border_color}
                onChange={(value) => setFaqSettings({ ...faqSettings, faq_border_color: value })}
                type="color"
                helpText="Border color"
              />
            </div>
          </InlineStack>
        </BlockStack>
      </Card>

      <Card>
        <InlineStack align="space-between">
          <Text variant="headingMd" as="h2">
            FAQ Management
          </Text>
          <Button primary onClick={handleAdd}>
            Add FAQ
          </Button>
        </InlineStack>
      </Card>

      {faqs.length === 0 ? (
        <Card>
          <EmptyState
            heading="No FAQs yet"
            action={{ content: 'Add FAQ', onAction: handleAdd }}
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
          >
            <p>Add frequently asked questions to help your customers.</p>
          </EmptyState>
        </Card>
      ) : (
        <Card>
          <DataTable
            columnContentTypes={['numeric', 'text', 'text', 'text']}
            headings={['Order', 'Question', 'Answer', 'Actions']}
            rows={tableRows}
          />
        </Card>
      )}

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingFaq ? 'Edit FAQ' : 'Add FAQ'}
        primaryAction={{
          content: 'Save',
          onAction: handleSave,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setModalOpen(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <TextField
              label="Question"
              value={formData.question}
              onChange={(value) => setFormData({ ...formData, question: value })}
              multiline={2}
            />
            <TextField
              label="Answer"
              value={formData.answer}
              onChange={(value) => setFormData({ ...formData, answer: value })}
              multiline={5}
            />
            <TextField
              label="Display Order"
              type="number"
              value={formData.display_order.toString()}
              onChange={(value) =>
                setFormData({ ...formData, display_order: parseInt(value) || 0 })
              }
              helpText="Lower numbers appear first"
            />
          </BlockStack>
          </Modal.Section>
        </Modal>
      </BlockStack>
    );
  }

